import type { LoadedApp } from '@/features/page-editor/store/types'

export interface CogoApplicationBundle {
  schemaVersion: 'cogo-application/v1'
  manifest: Record<string, unknown>
  blocks: Record<string, unknown>
  data?: Record<string, unknown>
  flows?: Record<string, unknown>
  tokens?: unknown
  designSystem?: unknown
  ontology?: unknown
  metadata?: Record<string, unknown>
}

export interface ImportedCogoApplication {
  id: string
  name: string
  app: LoadedApp
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value))
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return isRecord(value) ? value : undefined
}

function normalizeObjectRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

function toSafeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'imported-cogo-app'
}

function stripKnownJsonExtension(name: string): string {
  return name
    .replace(/\.cogo-app\.json$/i, '')
    .replace(/\.cogo\.json$/i, '')
    .replace(/\.json$/i, '')
}

function normalizeBlockRecord(value: unknown): Record<string, unknown> {
  if (isRecord(value)) return value
  if (!Array.isArray(value)) return {}

  return Object.fromEntries(
    value
      .filter(isRecord)
      .map((block, index) => {
        const name = readString(block.name) ?? readString(block.id) ?? `block-${index + 1}`
        return [name, block]
      }),
  )
}

function emptyRoot(name: string): Record<string, unknown> {
  return {
    id: `${name}-root`,
    type: 'container',
    children: [],
  }
}

function isFlowLike(value: Record<string, unknown>): boolean {
  return Array.isArray(value.steps) || value.trigger !== undefined || value.name !== undefined || value.id !== undefined
}

function putFlowDefinition(
  flows: Record<string, unknown>,
  preferredKey: string | undefined,
  flow: Record<string, unknown>,
): void {
  const baseKey = preferredKey?.trim() || `flow-${Object.keys(flows).length + 1}`
  let key = baseKey
  let suffix = 2
  while (key in flows) {
    key = `${baseKey}__${suffix}`
    suffix += 1
  }
  flows[key] = flow
}

function normalizeFlowDefinitions(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    const flows: Record<string, unknown> = {}
    value
      .filter(isRecord)
      .filter(isFlowLike)
      .forEach((flow, index) => {
        const name = readString(flow.name) ?? readString(flow.id) ?? `flow-${index + 1}`
        putFlowDefinition(flows, readString(flow.id) ?? name, {
          ...flow,
          name,
          steps: Array.isArray(flow.steps) ? flow.steps : [],
        })
      })
    return flows
  }

  if (!isRecord(value)) return {}

  if (isRecord(value.flows) || Array.isArray(value.flows)) {
    return normalizeFlowDefinitions(value.flows)
  }

  if (isFlowLike(value)) {
    const name = readString(value.name) ?? readString(value.id) ?? 'flow'
    return {
      [name]: {
        ...value,
        name,
        steps: Array.isArray(value.steps) ? value.steps : [],
      },
    }
  }

  const flows: Record<string, unknown> = {}
  for (const [name, flow] of Object.entries(value)) {
    if (!isRecord(flow) || !isFlowLike(flow)) continue
    const flowName = readString(flow.name) ?? readString(flow.id) ?? name
    putFlowDefinition(flows, name, {
      ...flow,
      name: flowName,
      steps: Array.isArray(flow.steps) ? flow.steps : [],
    })
  }
  return flows
}

function mergeFlowDefinitions(...maps: Array<Record<string, unknown>>): Record<string, unknown> {
  const merged: Record<string, unknown> = {}
  for (const map of maps) {
    for (const [key, flow] of Object.entries(map)) {
      if (!isRecord(flow) || !isFlowLike(flow)) continue
      if (key in merged) continue
      merged[key] = flow
    }
  }
  return merged
}

function normalizeBlockFlow(value: unknown): Record<string, unknown> {
  const flows = normalizeFlowDefinitions(value)
  if (Object.keys(flows).length === 0) return {}
  return { flows }
}

function normalizeBlock(name: string, rawBlock: unknown): Record<string, unknown> {
  const block = isRecord(rawBlock) ? rawBlock : {}
  const packageBlock = asRecord(block.block)
  const rawUi = asRecord(block.ui) ?? {}
  const root = asRecord(rawUi.root) ?? asRecord(block.root) ?? emptyRoot(name)
  const meta = {
    id: name,
    name,
    type: 'ui',
    version: '1.0.0',
    ...(packageBlock ?? {}),
    ...(asRecord(block.manifest) ?? {}),
    ...(asRecord(block.meta) ?? {}),
  }
  const state = normalizeObjectRecord(block.state ?? block.variables)
  const data = normalizeObjectRecord(block.data ?? block.datasource)
  const flow = normalizeBlockFlow(block.flow)
  const ui = {
    schemaVersion: readString(rawUi.schemaVersion) ?? readString(block.schemaVersion) ?? '3.0',
    blockType:
      readString(rawUi.blockType) ??
      readString(block.blockType) ??
      readString(packageBlock?.blockType) ??
      name,
    ...rawUi,
    root,
  }

  return {
    ...block,
    meta,
    ui,
    events: normalizeObjectRecord(block.events),
    flow,
    state,
    data,
    schemaVersion: readString(block.schemaVersion) ?? readString(ui.schemaVersion) ?? '3.0',
    blockType:
      readString(block.blockType) ??
      readString(packageBlock?.blockType) ??
      readString(ui.blockType) ??
      name,
    root,
    variables: normalizeObjectRecord(block.variables ?? block.state),
    datasource: normalizeObjectRecord(block.datasource ?? block.data),
    manifest: asRecord(block.manifest) ?? packageBlock ?? meta,
  }
}

function normalizeBlocks(value: unknown): Record<string, unknown> {
  const rawBlocks = normalizeBlockRecord(value)
  return Object.fromEntries(
    Object.entries(rawBlocks).map(([name, block]) => [name, normalizeBlock(name, block)]),
  )
}

function defaultBlockLayout(blocks: string[]): Record<string, { width: string; gap?: string }> {
  return Object.fromEntries(
    blocks.map((blockName, index) => [
      blockName,
      {
        width: 'full',
        ...(index > 0 ? { gap: 'mt-4' } : {}),
      },
    ]),
  )
}

function normalizePages(
  pages: unknown,
  blocks: Record<string, unknown>,
): Array<Record<string, unknown>> {
  const blockNames = Object.keys(blocks)
  const rawPages = Array.isArray(pages) ? pages.filter(isRecord) : []
  const normalizedPages = rawPages.map((page, index) => {
    const pageBlocks = Array.isArray(page.blocks)
      ? page.blocks.filter((item): item is string => typeof item === 'string')
      : []
    const effectiveBlocks = pageBlocks.length > 0 ? pageBlocks : blockNames
    const id = readString(page.id) ?? readString(page.name) ?? `page-${index + 1}`
    return {
      ...page,
      id,
      route: readString(page.route) ?? (index === 0 ? '/' : `/${id}`),
      name: readString(page.name) ?? readString(page.title) ?? id,
      title: readString(page.title) ?? readString(page.name) ?? id,
      layout: readString(page.layout) ?? 'flow',
      blocks: effectiveBlocks,
      blockLayout: asRecord(page.blockLayout) ?? defaultBlockLayout(effectiveBlocks),
    }
  })

  if (normalizedPages.length > 0) return normalizedPages

  return [{
    id: 'home',
    route: '/',
    name: 'Home',
    title: 'Home',
    layout: 'flow',
    blocks: blockNames,
    blockLayout: defaultBlockLayout(blockNames),
  }]
}

function normalizeSourceFrameworkRoute(value: string): string {
  const suffixIndex = value.search(/[?#]/)
  const pathname = suffixIndex >= 0 ? value.slice(0, suffixIndex) : value
  const suffix = suffixIndex >= 0 ? value.slice(suffixIndex) : ''
  let normalized = pathname.trim()
  normalized = normalized.replace(/\/\([^/)]+\)/g, '')
  normalized = normalized.replace(/^\([^/)]+\)\//, '/')
  normalized = normalized.replace(/\/index$/, '')
  normalized = normalized.replace(/\/+/g, '/')
  if (!normalized.startsWith('/')) normalized = `/${normalized}`
  if (normalized.length > 1) normalized = normalized.replace(/\/$/, '')
  return `${normalized}${suffix}`
}

function routePatternMatches(pattern: string, target: string): boolean {
  const patternPath = normalizeSourceFrameworkRoute(pattern).split(/[?#]/, 1)[0]
  const targetPath = normalizeSourceFrameworkRoute(target).split(/[?#]/, 1)[0]
  const patternParts = patternPath.split('/')
  const targetParts = targetPath.split('/')
  if (patternParts.length !== targetParts.length) return false
  return patternParts.every((part, index) => part.startsWith(':') || part === targetParts[index])
}

function normalizeNavigateTargetForPages(
  target: string,
  pages: Array<Record<string, unknown>>,
): string {
  if (!target.startsWith('/') && !target.startsWith('./') && !target.startsWith('../')) {
    return target
  }
  const normalized = normalizeSourceFrameworkRoute(target)
  if (normalized === '/' && !pages.some((page) => readString(page.route) === '/')) {
    return readString(pages[0]?.route) ?? target
  }
  if (normalized === target) return target
  const resolvesToPage = pages.some((page) => {
    const route = readString(page.route)
    return route ? routePatternMatches(route, normalized) : false
  })
  return resolvesToPage ? normalized : target
}

function normalizeNavigateSteps(
  value: unknown,
  pages: Array<Record<string, unknown>>,
): unknown {
  if (Array.isArray(value)) {
    return value.map((entry) => normalizeNavigateSteps(entry, pages))
  }
  if (!isRecord(value)) return value

  const normalized = Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, normalizeNavigateSteps(entry, pages)]),
  )
  if (readString(value.type) !== 'navigate') return normalized
  const target = readString(value.target)
  if (!target) return normalized
  return {
    ...normalized,
    target: normalizeNavigateTargetForPages(target, pages),
  }
}

function normalizeNavigateFlows(
  flows: Record<string, unknown>,
  pages: Array<Record<string, unknown>>,
): Record<string, unknown> {
  return normalizeNavigateSteps(flows, pages) as Record<string, unknown>
}

function normalizeBlockNavigateFlows(
  blocks: Record<string, unknown>,
  pages: Array<Record<string, unknown>>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(blocks).map(([name, block]) => {
      if (!isRecord(block)) return [name, block]
      return [
        name,
        {
          ...block,
          flow: normalizeNavigateSteps(block.flow, pages),
        },
      ]
    }),
  )
}

function collectFlowsFromBlocks(blocks: Record<string, unknown>): Record<string, unknown> {
  const flows: Record<string, unknown> = {}
  for (const block of Object.values(blocks)) {
    if (!isRecord(block)) continue
    const flowMap = normalizeFlowDefinitions(block.flow)
    for (const [flowName, flowDef] of Object.entries(flowMap)) {
      if (flowName.startsWith('_') || !isRecord(flowDef)) continue
      if (!('steps' in flowDef) && !('trigger' in flowDef)) continue
      putFlowDefinition(flows, flowName, flowDef)
    }
  }
  return flows
}

function collectFlowsFromOntology(ontology: unknown): Record<string, unknown> {
  const ontologyRecord = asRecord(ontology)
  return normalizeFlowDefinitions(ontologyRecord?.actionflows)
}

function ontologyEvidenceScore(value: unknown): number {
  const ontology = asRecord(value)
  if (!ontology) return 0
  const countArray = (key: string) => Array.isArray(ontology[key]) ? ontology[key].length : 0
  return (
    countArray('pages') +
    countArray('states') +
    countArray('datasources') +
    countArray('actions') +
    countArray('actionflows') +
    countArray('sourceFiles')
  )
}

function chooseSourceRichOntology(primary: unknown, fallback: unknown): unknown {
  if (ontologyEvidenceScore(fallback) > ontologyEvidenceScore(primary)) return fallback
  return primary ?? fallback
}

export function normalizeCogoApplicationBundle(
  input: unknown,
  sourceName = 'Imported Coogo App',
): ImportedCogoApplication {
  if (!isRecord(input)) {
    throw new Error('Invalid Coogo application: expected a JSON object')
  }
  // Canonical Cogo Application Packages name this document `app`; Studio's
  // exported bundle names the same application manifest `manifest`.
  const rawManifest = asRecord(input.manifest) ?? asRecord(input.app)
  const rawBlocks = input.blocks
  if (!rawManifest || rawBlocks == null) {
    throw new Error('Invalid Coogo application: missing manifest or blocks')
  }

  const normalizedBlocks = normalizeBlocks(rawBlocks)
  if (Object.keys(normalizedBlocks).length === 0) {
    throw new Error('Invalid Coogo application: no blocks found')
  }

  const fallbackName = stripKnownJsonExtension(sourceName)
  const appId = readString(rawManifest.appId) ?? readString(input.appId) ?? toSafeSlug(fallbackName)
  const name = readString(rawManifest.name) ?? fallbackName
  const pages = normalizePages(rawManifest.pages, normalizedBlocks)
  const blocks = normalizeBlockNavigateFlows(normalizedBlocks, pages)
  const collectedFlows = collectFlowsFromBlocks(blocks)
  const flows = normalizeNavigateFlows(
    mergeFlowDefinitions(
      collectedFlows,
      normalizeFlowDefinitions(input.flows),
      collectFlowsFromOntology(input.ontology),
    ),
    pages,
  )
  const manifest = {
    ...rawManifest,
    appId,
    name,
    version: readString(rawManifest.version) ?? '1.0.0',
    schemaVersion: '3.0',
    pages,
    datasources: normalizeObjectRecord(rawManifest.datasources),
    globalState: normalizeObjectRecord(rawManifest.globalState),
  }
  const metadata = {
    ...normalizeObjectRecord(input.metadata),
    cogoApplication: {
      schemaVersion:
        readString(input.schemaVersion) ??
        readString(input.packageVersion) ??
        'unknown',
      sourceName,
      importedAt: new Date().toISOString(),
    },
    ...(input.ontology !== undefined ? { cogoApplicationOntology: input.ontology } : {}),
  }

  const app = {
    manifest,
    blocks,
    data: normalizeObjectRecord(input.data),
    flows,
    tokens: input.tokens,
    designSystem: input.designSystem,
    ...(input.ontology !== undefined ? { ontology: input.ontology } : {}),
    metadata,
  } as unknown as LoadedApp

  return {
    id: `import-${toSafeSlug(appId)}`,
    name,
    app,
  }
}

export function exportLoadedAppAsCogoApplication(
  app: LoadedApp,
  options: { projectId?: string } = {},
): CogoApplicationBundle {
  const appRecord = app as unknown as Record<string, unknown>
  const metadata = normalizeObjectRecord(appRecord.metadata)
  const ontology = chooseSourceRichOntology(appRecord.ontology, metadata.cogoApplicationOntology)
  const blocks = normalizeBlocks(app.blocks)
  const blockFlows = collectFlowsFromBlocks(blocks)
  const ontologyFlows = collectFlowsFromOntology(ontology)
  const flows = mergeFlowDefinitions(normalizeFlowDefinitions(app.flows), blockFlows, ontologyFlows)
  const manifest = {
    ...(app.manifest as unknown as Record<string, unknown>),
    schemaVersion: '3.0',
  }

  return {
    schemaVersion: 'cogo-application/v1',
    manifest,
    blocks,
    data: app.data,
    flows,
    ...(app.tokens !== undefined ? { tokens: app.tokens } : {}),
    ...(app.designSystem !== undefined ? { designSystem: app.designSystem } : {}),
    ...(ontology !== undefined ? { ontology } : {}),
    metadata: {
      ...metadata,
      exportedAt: new Date().toISOString(),
      ...(options.projectId ? { projectId: options.projectId } : {}),
    },
  }
}

export function cogoApplicationDownloadName(app: LoadedApp): string {
  const manifest = app.manifest as unknown as Record<string, unknown>
  const name = readString(manifest.name) ?? readString(manifest.appId) ?? 'cogo-application'
  return `${toSafeSlug(name)}.cogo-app.json`
}

export function downloadCogoApplicationBundle(app: LoadedApp, projectId?: string): void {
  const bundle = exportLoadedAppAsCogoApplication(app, { projectId })
  const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = cogoApplicationDownloadName(app)
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
